import { useEffect, useState } from "react";
import { apiFetch } from "../../lib/api.js";
import {
  AI_PROVIDER_OPTIONS,
  FOUNDRY_ENDPOINT_ERROR,
  FOUNDRY_ENDPOINT_PLACEHOLDER,
  isValidFoundryEndpoint,
  providerServesFamily,
  webSearchHelp,
  type AiProviderConfigShape,
  type AiProviderKind,
  type ModelCandidates
} from "../../lib/aiProvider.js";
import type { IntegrationFormProps } from "./types.js";

/**
 * ADR-0207 §3.1 — the AI provider kind's create sub-form. Provider, API key,
 * and for Azure AI Foundry the resource endpoint and one deployment name per
 * model. Web search appears only when the chosen provider can serve the hub's
 * web-search model. The name is server-fixed, so there is none to enter.
 */

export function AiProviderIntegrationForm(props: IntegrationFormProps): JSX.Element {
  const config = props.config as Partial<AiProviderConfigShape>;
  const apiKey = typeof props.credentials?.api_key === "string" ? props.credentials.api_key : "";
  const provider: AiProviderKind = config.provider_kind ?? "anthropic";

  const [candidates, setCandidates] = useState<ModelCandidates | null>(null);
  const [candidatesError, setCandidatesError] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [endpointError, setEndpointError] = useState<string | null>(null);

  const emit = (next: Partial<AiProviderConfigShape>, nextKey: string = apiKey) => {
    const merged: AiProviderConfigShape = {
      provider_kind: next.provider_kind ?? provider,
      web_search_enabled: next.web_search_enabled ?? config.web_search_enabled ?? false,
      ...((next.provider_kind ?? provider) === "azure_foundry"
        ? { endpoint: next.endpoint ?? config.endpoint ?? "", deployments: next.deployments ?? config.deployments ?? {} }
        : {})
    };
    props.onChange({ config: merged as unknown as Record<string, unknown>, credentials: { api_key: nextKey } });
  };

  // The create body must name a provider even if the admin never touches the select.
  useEffect(() => {
    if (!config.provider_kind) emit({ provider_kind: "anthropic", web_search_enabled: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        setCandidates(await apiFetch<ModelCandidates>("/api/tenant/admin/ai-provider/model-candidates"));
      } catch (e) {
        setCandidatesError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  const deployments = config.deployments ?? {};
  const showWebSearch = providerServesFamily(provider, candidates?.web_search_model_family);
  const webHelp = webSearchHelp(provider);

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <label className="field">
        <span className="label">Provider</span>
        <select
          className="select"
          value={provider}
          onChange={(e) => {
            // Changing provider clears everything below it.
            setEndpointError(null);
            emit(
              { provider_kind: e.target.value as AiProviderKind, web_search_enabled: false, endpoint: "", deployments: {} },
              ""
            );
          }}
        >
          {AI_PROVIDER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      <label className="field">
        <span className="label">API key</span>
        <div style={{ display: "flex", gap: 6 }}>
          <input
            className="input"
            type={showKey ? "text" : "password"}
            value={apiKey}
            onChange={(e) => emit({}, e.target.value)}
            autoComplete="off"
            style={{ flex: 1 }}
          />
          <button type="button" className="btn" onClick={() => setShowKey((v) => !v)} aria-label={showKey ? "Hide key" : "Show key"}>
            {showKey ? "Hide" : "Show"}
          </button>
        </div>
        <div className="help">Stored encrypted. Atlas never shows it again.</div>
      </label>

      {provider === "azure_foundry" ? (
        <>
          <label className="field">
            <span className="label">Resource endpoint</span>
            <input
              className="input"
              type="text"
              value={config.endpoint ?? ""}
              onChange={(e) => emit({ endpoint: e.target.value })}
              onBlur={() => {
                const v = (config.endpoint ?? "").trim();
                setEndpointError(v === "" || isValidFoundryEndpoint(v) ? null : FOUNDRY_ENDPOINT_ERROR);
              }}
              placeholder={FOUNDRY_ENDPOINT_PLACEHOLDER}
            />
            {endpointError ? <div className="help" style={{ color: "var(--danger-text)" }}>{endpointError}</div> : null}
          </label>

          <div className="field">
            <span className="label">Deployments</span>
            <div className="help">
              Enter the deployment name for each model you&rsquo;ve deployed in Foundry. Leave a row blank if that model
              isn&rsquo;t deployed — Atlas won&rsquo;t offer it.
            </div>
            {candidatesError ? (
              <div className="help" style={{ color: "var(--danger-text)" }}>{candidatesError}</div>
            ) : !candidates ? (
              <div className="muted">Loading…</div>
            ) : (
              <div className="tableWrap">
                <table className="table tableCompact">
                  <thead>
                    <tr>
                      <th>Model</th>
                      <th>Deployment name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.candidates.map((c) => (
                      <tr key={c.model_id}>
                        <td className="mono">{c.model_id}</td>
                        <td>
                          <input
                            className="input"
                            type="text"
                            value={deployments[c.model_id] ?? ""}
                            placeholder={c.model_id}
                            onChange={(e) => emit({ deployments: { ...deployments, [c.model_id]: e.target.value } })}
                            aria-label={`Deployment name for ${c.model_id}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : null}

      {showWebSearch ? (
        <label className="field">
          <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={config.web_search_enabled === true}
              onChange={(e) => emit({ web_search_enabled: e.target.checked })}
            />
            <span className="label" style={{ margin: 0 }}>Allow web search</span>
          </span>
          {webHelp ? <div className="help">{webHelp}</div> : null}
        </label>
      ) : null}
    </div>
  );
}
